/**
 * RETAIL ELITE — collection.js
 * Ultra-Premium Shopify 2.0 Collection Page Controller
 *
 * Features
 * ─────────────────────────────────────────────────────
 * • Grid view switcher (2 / 3 / 4 columns) with persistence
 * • Sort-by select → URL push with smooth transition
 * • Mobile filter drawer (cloned from sidebar)
 * • Active filter chips in toolbar
 * • Quick-add to cart (optimistic, no reload)
 * • Wishlist toggle (localStorage, persistent)
 * • Hero parallax (CSS-var driven, rAF throttled)
 * • Card entrance stagger via CSS --card-index
 * • Accessible toast notifications
 * • URL state management (replaceState for sort, pushState for filters)
 */

(function () {
  'use strict';

  /* ════════════════════════════════════════════════════════
     CONSTANTS
  ════════════════════════════════════════════════════════ */
  const TOAST_MS         = 3000;
  const WISHLIST_KEY     = 'pe_wishlist';
  const VIEW_PREF_KEY    = 'pe_col_view';
  const DEFAULT_VIEW     = 4;

  /* ════════════════════════════════════════════════════════
     DOM CACHE
  ════════════════════════════════════════════════════════ */
  const page         = document.querySelector('[data-collection-page]');
  if (!page) return;

  const grid         = document.querySelector('[data-product-grid]');
  const sortSelect   = document.querySelector('[data-sort-select]');
  const viewBtns     = document.querySelectorAll('[data-view]');
  const filterToggle = document.querySelector('[data-filter-toggle]');
  const filterDrawer = document.querySelector('[data-filter-drawer]');
  const filterOverlay= document.querySelector('[data-filter-overlay]');
  const filterClose  = document.querySelector('[data-filter-close]');
  const filterApply  = document.querySelector('[data-filter-apply]');
  const drawerBody   = document.querySelector('.cl-filter-drawer__body');
  const filtersAside = document.querySelector('[data-filters]');
  const clearBtns    = document.querySelectorAll('[data-clear-filters]');
  const activeChipEl = document.querySelector('[data-active-filters]');
  const activeCountEl= document.querySelector('[data-active-filter-count]');
  const heroBackdrop = document.querySelector('[data-hero] .cl-hero__backdrop');
  const toast        = document.querySelector('[data-toast]');


  /* ════════════════════════════════════════════════════════
     UTILITY FUNCTIONS
  ════════════════════════════════════════════════════════ */

  let toastTimer;

  function showToast(message, type = 'default') {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.className   = 'cl-toast cl-toast--visible';
    if (type === 'success') toast.classList.add('cl-toast--success');
    if (type === 'error')   toast.classList.add('cl-toast--error');
    toastTimer = setTimeout(() => toast.classList.remove('cl-toast--visible'), TOAST_MS);
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  async function cartAdd(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  /** Persist and restore scroll position across sort/filter navigations */
  function saveScroll() { sessionStorage.setItem('cl_scroll', window.scrollY); }

  function restoreScroll() {
    const y = sessionStorage.getItem('cl_scroll');
    if (y) { window.scrollTo(0, parseInt(y, 10)); sessionStorage.removeItem('cl_scroll'); }
  }


  /* ════════════════════════════════════════════════════════
     GRID VIEW SWITCHER
  ════════════════════════════════════════════════════════ */

  function setView(cols, save = true) {
    if (!grid) return;

    /* Remove all column classes and apply the new one */
    grid.className = grid.className.replace(/\bcl-grid--\d\b/g, '').trim();
    grid.classList.add(`cl-grid--${cols}`);

    /* Update button states */
    viewBtns.forEach(btn => {
      const active = parseInt(btn.dataset.view, 10) === cols;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', String(active));
    });

    if (save) localStorage.setItem(VIEW_PREF_KEY, String(cols));
  }

  function initView() {
    /* Screen-size aware default */
    const screenDefault = window.innerWidth <= 768 ? 2 : DEFAULT_VIEW;
    const saved = parseInt(localStorage.getItem(VIEW_PREF_KEY), 10) || screenDefault;
    setView(saved, false);

    viewBtns.forEach(btn => {
      btn.addEventListener('click', () => setView(parseInt(btn.dataset.view, 10)));
    });
  }


  /* ════════════════════════════════════════════════════════
     SORT SELECT
  ════════════════════════════════════════════════════════ */

  function initSort() {
    if (!sortSelect) return;
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      saveScroll();
      window.location.href = url.toString();
    });
  }


  /* ════════════════════════════════════════════════════════
     ACTIVE FILTER CHIPS  (toolbar display)
  ════════════════════════════════════════════════════════ */

  function buildActiveChips() {
    if (!activeChipEl) return;

    const params = new URLSearchParams(window.location.search);
    const chips  = [];

    const labelMap = {
      'filter.v.vendor':          'Brand',
      'filter.v.availability':    'Availability',
      'filter.v.price.gte':       'Min Price',
      'filter.v.price.lte':       'Max Price',
    };

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      const label = labelMap[key] || key.replace('filter.v.', '').replace(/\./g, ' ');
      const displayVal = key.includes('price') ? formatMoney(parseInt(value, 10)) : value;
      chips.push({ key, value, label, displayVal });
    });

    if (chips.length === 0) {
      activeChipEl.innerHTML  = '';
      if (activeCountEl) activeCountEl.hidden = true;
      return;
    }

    if (activeCountEl) {
      activeCountEl.hidden      = false;
      activeCountEl.textContent = chips.length;
    }

    activeChipEl.innerHTML = chips.map(chip => {
      const removeUrl = buildRemoveUrl(chip.key, chip.value);
      return `<a class="cl-active-chip" href="${removeUrl}" aria-label="Remove ${chip.label} filter">
        ${chip.label}: ${chip.displayVal}
        <span class="cl-active-chip__remove" aria-hidden="true">✕</span>
      </a>`;
    }).join('');
  }

  function buildRemoveUrl(key, value) {
    const url    = new URL(window.location.href);
    const params = url.searchParams;
    /* Delete the specific key-value pair */
    const existing = params.getAll(key);
    params.delete(key);
    existing.filter(v => v !== value).forEach(v => params.append(key, v));
    return url.toString();
  }


  /* ════════════════════════════════════════════════════════
     MOBILE FILTER DRAWER
  ════════════════════════════════════════════════════════ */

  function openFilterDrawer() {
    if (!filterDrawer || !filterOverlay) return;

    /* Clone sidebar content into drawer on first open */
    if (filtersAside && drawerBody && drawerBody.children.length === 0) {
      drawerBody.innerHTML = filtersAside.innerHTML;
    }

    filterDrawer.classList.add('cl-filter-drawer--open');
    filterOverlay.classList.add('cl-filter-overlay--open');
    filterToggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    /* Trap focus */
    const firstFocusable = filterDrawer.querySelector('button, a, input, select');
    firstFocusable?.focus();
  }

  function closeFilterDrawer() {
    if (!filterDrawer || !filterOverlay) return;
    filterDrawer.classList.remove('cl-filter-drawer--open');
    filterOverlay.classList.remove('cl-filter-overlay--open');
    filterToggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    filterToggle?.focus();
  }

  function initFilterDrawer() {
    filterToggle?.addEventListener('click', () => {
      const isOpen = filterDrawer?.classList.contains('cl-filter-drawer--open');
      isOpen ? closeFilterDrawer() : openFilterDrawer();
    });

    filterClose?.addEventListener('click', closeFilterDrawer);
    filterOverlay?.addEventListener('click', closeFilterDrawer);
    filterApply?.addEventListener('click', closeFilterDrawer);

    clearBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const url = new URL(window.location.href);
        /* Strip all filter params */
        Array.from(url.searchParams.keys())
          .filter(k => k.startsWith('filter.'))
          .forEach(k => url.searchParams.delete(k));
        saveScroll();
        window.location.href = url.toString();
      });
    });

    /* Keyboard: Escape closes */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && filterDrawer?.classList.contains('cl-filter-drawer--open')) {
        closeFilterDrawer();
      }
    });
  }


  /* ════════════════════════════════════════════════════════
     QUICK ADD TO CART
  ════════════════════════════════════════════════════════ */

  function initQuickAdd() {
    if (!grid) return;

    grid.addEventListener('click', async e => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;

      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const original = btn.innerHTML;
      btn.classList.add('cl-card__quick-add--loading');
      btn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
          style="animation: cl-spin .6s linear infinite" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
        </svg>
        Adding…`;

      try {
        await cartAdd(variantId);

        /* Update cart count bubble if present in theme header */
        const cartBubble = document.querySelector('.cart-count, [data-cart-count], .cart-item-count');
        if (cartBubble) {
          const current = parseInt(cartBubble.textContent, 10) || 0;
          cartBubble.textContent = current + 1;
        }

        btn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          Added!`;
        btn.style.background = 'var(--pe-green)';
        btn.style.color      = 'var(--pe-white)';

        showToast('Added to cart!', 'success');

        setTimeout(() => {
          btn.innerHTML = original;
          btn.style.background = '';
          btn.style.color      = '';
          btn.classList.remove('cl-card__quick-add--loading');
        }, 1800);

      } catch (err) {
        btn.innerHTML = original;
        btn.classList.remove('cl-card__quick-add--loading');
        showToast(err.message || 'Could not add to cart', 'error');
      }
    });
  }


  /* ════════════════════════════════════════════════════════
     WISHLIST
  ════════════════════════════════════════════════════════ */

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); }
    catch { return []; }
  }

  function saveWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
    catch { /* storage full */ }
  }

  function toggleWishlist(productId) {
    const list    = getWishlist();
    const idx     = list.indexOf(productId);
    const removed = idx !== -1;

    if (removed) {
      list.splice(idx, 1);
    } else {
      list.push(productId);
    }

    saveWishlist(list);
    return !removed; /* true = now wishlisted */
  }

  function syncWishlistUI() {
    const list = getWishlist();
    document.querySelectorAll('[data-wishlist]').forEach(btn => {
      const id = btn.dataset.productId;
      btn.classList.toggle('cl-card__wishlist--active', list.includes(id));
      btn.setAttribute('aria-pressed', String(list.includes(id)));
    });
  }

  function initWishlist() {
    syncWishlistUI();

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-wishlist]');
      if (!btn) return;

      e.preventDefault();
      const id        = btn.dataset.productId;
      const wishlisted = toggleWishlist(id);

      btn.classList.toggle('cl-card__wishlist--active', wishlisted);
      btn.setAttribute('aria-pressed', String(wishlisted));
      showToast(wishlisted ? 'Saved to wishlist' : 'Removed from wishlist');
    });
  }


  /* ════════════════════════════════════════════════════════
     HERO PARALLAX  (rAF throttled)
  ════════════════════════════════════════════════════════ */

  function initHeroParallax() {
    if (!heroBackdrop || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;

    function onScroll() {
      if (ticking) return;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const rate    = scrollY * 0.25;
        heroBackdrop.style.transform = `scale(1.04) translateY(${rate}px)`;
        ticking = false;
      });
      ticking = true;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }


  /* ════════════════════════════════════════════════════════
     TOOLBAR STICKY SHADOW
  ════════════════════════════════════════════════════════ */

  function initToolbarShadow() {
    const toolbar = document.querySelector('[data-toolbar]');
    if (!toolbar) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        toolbar.style.boxShadow = entry.isIntersecting
          ? 'none'
          : '0 2px 12px rgba(0,0,0,.08)';
      },
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    );

    /* Observe a sentinel element placed above the toolbar */
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'height:1px;pointer-events:none;position:absolute;top:0;width:100%;';
    toolbar.before(sentinel);
    observer.observe(sentinel);
  }


  /* ════════════════════════════════════════════════════════
     FILTER GROUP LABELS (mobile drawer keyboard)
  ════════════════════════════════════════════════════════ */

  function initFilterKeyboard() {
    document.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const summary = e.target.closest('.cl-filter-group__label');
      if (!summary) return;
      e.preventDefault();
      summary.click();
    });
  }


  /* ════════════════════════════════════════════════════════
     CSS SPIN KEYFRAME (for quick-add spinner)
  ════════════════════════════════════════════════════════ */

  function injectSpinKeyframe() {
    if (document.getElementById('cl-spin-kf')) return;
    const style = document.createElement('style');
    style.id = 'cl-spin-kf';
    style.textContent = '@keyframes cl-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }


  /* ════════════════════════════════════════════════════════
     INIT
  ════════════════════════════════════════════════════════ */

  function init() {
    injectSpinKeyframe();
    initView();
    initSort();
    buildActiveChips();
    initFilterDrawer();
    initQuickAdd();
    initWishlist();
    initHeroParallax();
    initToolbarShadow();
    initFilterKeyboard();
    restoreScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();